// src/components/clients/CustomerDetailedMetrics.jsx
import React, { useState, useEffect } from 'react';
import { get } from '../../helpers/apiHelper';
import {
    FaUser, FaShoppingCart, FaClock, FaCalendar, FaGift, FaTag,
    FaChartLine, FaDollarSign, FaUsers, FaStar, FaArrowUp, FaArrowDown
} from 'react-icons/fa';

const CustomerDetailedMetrics = ({ customerId, period = '12' }) => {
    const [customerData, setCustomerData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (customerId) {
            fetchCustomerMetrics();
        }
    }, [customerId, period]);

    const fetchCustomerMetrics = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await get(`clientes/${customerId}/analytics/detailed-metrics?period=${period}`);
            setCustomerData(data);
        } catch (error) {
            console.error('Error fetching customer metrics:', error);
            setError('No se pudieron cargar las métricas detalladas');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-ES');
    };

    const getSegmentColor = (segmentName) => {
        const colors = {
            'Champions': 'bg-green-100 text-green-800',
            'Loyal Customers': 'bg-blue-100 text-blue-800',
            'At Risk': 'bg-yellow-100 text-yellow-800',
            'Can\'t Lose': 'bg-red-100 text-red-800',
            'About to Sleep': 'bg-orange-100 text-orange-800',
            'Lost': 'bg-gray-100 text-gray-800'
        };
        return colors[segmentName] || 'bg-gray-100 text-gray-800';
    };

    const getBehaviorSegmentColor = (segmentName) => {
        const colors = {
            'Fin de semana': 'bg-purple-100 text-purple-800',
            'Nocturnos': 'bg-indigo-100 text-indigo-800',
            'Cazapromos': 'bg-pink-100 text-pink-800',
            'Ticket alto': 'bg-emerald-100 text-emerald-800',
            'Express': 'bg-cyan-100 text-cyan-800',
            'Recurrentes': 'bg-teal-100 text-teal-800',
            'Exploradores': 'bg-amber-100 text-amber-800',
            'Especialistas': 'bg-violet-100 text-violet-800'
        };
        return colors[segmentName] || 'bg-gray-100 text-gray-800';
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow border p-6">
                <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="h-24 bg-gray-200 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-lg shadow border p-6">
                <div className="text-center text-red-500">
                    <FaUser className="mx-auto text-4xl mb-2 opacity-50" />
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    if (!customerData) {
        return (
            <div className="bg-white rounded-lg shadow border p-6">
                <div className="text-center text-gray-500">
                    <FaUser className="mx-auto text-4xl mb-2 opacity-50" />
                    <p>No hay datos disponibles para este cliente</p>
                </div>
            </div>
        );
    }

    const { rfmData, enhancedMetrics, additionalSegments, strategies } = customerData;

    return (
        <div className="bg-white rounded-lg shadow border">
            <div className="p-6 border-b">
                <h3 className="text-xl font-bold flex items-center">
                    <FaUser className="mr-3 text-blue-500" />
                    Análisis Detallado del Cliente
                </h3>
                <p className="text-gray-600 mt-1">
                    {rfmData?.nombre} {rfmData?.apellido} - Período: {period} meses
                </p>
            </div>

            <div className="p-6">
                {/* Métricas RFM */}
                <div className="mb-8">
                    <h4 className="text-lg font-semibold mb-4 flex items-center">
                        <FaChartLine className="mr-2 text-green-500" />
                        Análisis RFM
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-green-600 font-medium">Recencia</p>
                                    <p className="text-2xl font-bold text-green-700">{rfmData?.recency || 0}</p>
                                    <p className="text-xs text-green-600">días desde última compra</p>
                                </div>
                                <FaClock className="text-green-500 text-2xl" />
                            </div>
                        </div>
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-blue-600 font-medium">Frecuencia</p>
                                    <p className="text-2xl font-bold text-blue-700">{rfmData?.frequency || 0}</p>
                                    <p className="text-xs text-blue-600">compras en el período</p>
                                </div>
                                <FaCalendar className="text-blue-500 text-2xl" />
                            </div>
                        </div>
                        <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-purple-600 font-medium">Valor Monetario</p>
                                    <p className="text-2xl font-bold text-purple-700">${rfmData?.monetary || 0}</p>
                                    <p className="text-xs text-purple-600">valor total gastado</p>
                                </div>
                                <FaDollarSign className="text-purple-500 text-2xl" />
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between">
                        <div>
                            <span className="text-sm text-gray-600">Segmento RFM:</span>
                            <span className={`ml-2 px-3 py-1 rounded-full text-sm font-medium ${getSegmentColor(rfmData?.segment)}`}>
                                {rfmData?.segment || 'Sin segmento'}
                            </span>
                        </div>
                        <div>
                            <span className="text-sm text-gray-600">Score RFM:</span>
                            <span className="ml-2 font-bold text-lg">{rfmData?.rfmScore || 'N/A'}</span>
                        </div>
                    </div>
                </div>

                {/* Métricas de Comportamiento */}
                {enhancedMetrics && (
                    <div className="mb-8">
                        <h4 className="text-lg font-semibold mb-4 flex items-center">
                            <FaUsers className="mr-2 text-purple-500" />
                            Métricas de Comportamiento
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="bg-gray-50 p-4 rounded-lg border">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-600">Ticket Promedio</span>
                                    <FaShoppingCart className="text-gray-500" />
                                </div>
                                <p className="text-xl font-bold">${enhancedMetrics.avg_basket_size?.toFixed(2) || '0.00'}</p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-600">Intervalo Promedio</span>
                                    <FaClock className="text-gray-500" />
                                </div>
                                <p className="text-xl font-bold">{enhancedMetrics.ipi_avg?.toFixed(1) || '0'} días</p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-600">Día Preferido</span>
                                    <FaCalendar className="text-gray-500" />
                                </div>
                                <p className="text-xl font-bold">{enhancedMetrics.dow_mode || 'N/A'}</p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-600">Horario Preferido</span>
                                    <FaClock className="text-gray-500" />
                                </div>
                                <p className="text-xl font-bold">{enhancedMetrics.daypart_mode || 'N/A'}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div className="bg-gray-50 p-4 rounded-lg border">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-600">Uso de Promociones</span>
                                    <FaGift className="text-gray-500" />
                                </div>
                                <p className="text-xl font-bold">{(enhancedMetrics.promo_rate * 100)?.toFixed(1) || '0'}%</p>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm text-gray-600">Unidades Promedio</span>
                                    <FaShoppingCart className="text-gray-500" />
                                </div>
                                <p className="text-xl font-bold">{enhancedMetrics.avg_units?.toFixed(1) || '0'}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Segmentos Adicionales */}
                {additionalSegments && additionalSegments.length > 0 && (
                    <div className="mb-8">
                        <h4 className="text-lg font-semibold mb-4 flex items-center">
                            <FaTag className="mr-2 text-indigo-500" />
                            Segmentos de Comportamiento
                        </h4>
                        <div className="flex flex-wrap gap-2">
                            {additionalSegments.map((segment, index) => (
                                <span
                                    key={index}
                                    className={`px-3 py-1 rounded-full text-sm font-medium ${getBehaviorSegmentColor(segment)}`}
                                >
                                    {segment}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Distribución por Categorías */}
                {enhancedMetrics?.category_share && (
                    <div className="mb-8">
                        <h4 className="text-lg font-semibold mb-4 flex items-center">
                            <FaChartLine className="mr-2 text-orange-500" />
                            Distribución por Categorías
                        </h4>
                        <div className="space-y-2">
                            {Object.entries(enhancedMetrics.category_share).map(([category, percentage]) => (
                                <div key={category} className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">{category}</span>
                                    <div className="flex items-center">
                                        <div className="w-32 bg-gray-200 rounded-full h-2 mr-2">
                                            <div
                                                className="bg-orange-500 h-2 rounded-full"
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div>
                                        <span className="text-sm font-medium">{percentage}%</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Estrategias Recomendadas */}
                {strategies && (
                    <div className="mb-8">
                        <h4 className="text-lg font-semibold mb-4 flex items-center">
                            <FaStar className="mr-2 text-yellow-500" />
                            Estrategias Recomendadas
                        </h4>
                        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                            <p className="text-sm text-yellow-800 mb-2">
                                <strong>Prioridad:</strong> {strategies.priority}
                            </p>
                            <p className="text-sm text-yellow-800 mb-3">
                                <strong>Presupuesto:</strong> {strategies.budget}
                            </p>
                            <div>
                                <p className="text-sm font-medium text-yellow-800 mb-2">Estrategias:</p>
                                <ul className="list-disc list-inside space-y-1">
                                    {strategies.strategies.map((strategy, index) => (
                                        <li key={index} className="text-sm text-yellow-700">{strategy}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                {/* Información Adicional */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg border">
                        <h5 className="font-semibold mb-2">Última Compra</h5>
                        <p className="text-sm text-gray-600">
                            {rfmData?.lastPurchase ? formatDate(rfmData.lastPurchase) : 'N/A'}
                        </p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg border">
                        <h5 className="font-semibold mb-2">Total de Compras</h5>
                        <p className="text-sm text-gray-600">
                            {rfmData?.totalPurchases || 0} compras
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerDetailedMetrics;
